import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { analysisApi, type URLClickAnalysisParams } from '@/api/analysis';
import { Select, type SelectOption } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  MousePointer2,
  RefreshCw,
  Download,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import type { URLClickAnalysisResponse, URLClickByStep } from '@/types/data';

// チャネルオプション
const channelOptions: SelectOption[] = [
  { value: '', label: 'すべて' },
  { value: 'LINE', label: 'LINE' },
  { value: 'メール', label: 'メール' },
];

// ステップ詳細コンポーネント
interface StepDetailProps {
  step: URLClickByStep;
  isExpanded: boolean;
  onToggle: () => void;
}

function StepDetail({ step, isExpanded, onToggle }: StepDetailProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
          <span className="font-medium text-gray-900">{step.step_name}</span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              step.channel === 'LINE'
                ? 'bg-green-100 text-green-700'
                : 'bg-blue-100 text-blue-700'
            }`}
          >
            {step.channel}
          </span>
        </div>
        <span className="text-sm font-medium text-gray-600">
          {step.total_clicks.toLocaleString()} クリック
        </span>
      </button>

      {isExpanded && (
        <div className="p-4 space-y-2">
          {step.clicks.map((click, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
            >
              <div className="flex items-center gap-2">
                <MousePointer2 className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{click.tap_detail}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-900">
                  {click.click_count.toLocaleString()}
                </span>
                <span className="text-xs text-gray-500 w-16 text-right">
                  {click.percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function URLClickAnalysis() {
  const { accessibleCompanies } = useAuthStore();

  // 状態管理
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [channel, setChannel] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [data, setData] = useState<URLClickAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  // 初回法人設定
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // データ取得
  const fetchData = useCallback(async () => {
    if (!selectedCompanyId) return;

    setLoading(true);
    setError(null);

    try {
      const params: URLClickAnalysisParams = {
        company_id: selectedCompanyId,
        start_date: startDate,
        end_date: endDate,
      };

      if (channel) {
        params.channel = channel;
      }

      const result = await analysisApi.getURLClickAnalysis(params);
      setData(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, startDate, endDate, channel]);

  // 初回データ取得
  useEffect(() => {
    if (selectedCompanyId) {
      fetchData();
    }
  }, [selectedCompanyId]);

  // 法人オプション
  const companyOptions: SelectOption[] = accessibleCompanies.map((c) => ({
    value: c.id,
    label: c.display_name || c.name,
  }));

  // ステップ展開トグル
  const toggleStep = (stepKey: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepKey)) {
      newExpanded.delete(stepKey);
    } else {
      newExpanded.add(stepKey);
    }
    setExpandedSteps(newExpanded);
  };

  // CSVエクスポート
  const exportToCsv = () => {
    if (!data) return;

    const rows: string[] = [];

    // ヘッダー
    rows.push('"クリック先","クリック数","割合(%)"');

    // データ行
    for (const item of data.data) {
      rows.push(`"${item.tap_detail}",${item.click_count},${item.percentage}`);
    }

    // ダウンロード
    const csv = '\uFEFF' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `url_click_analysis_${startDate}_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // トップ/ボトムハイライト判定
  const getHighlightClass = (index: number, total: number): string => {
    if (total < 3) return '';
    if (index === 0) return 'bg-green-50 border-l-4 border-green-500';
    if (index === total - 1) return 'bg-red-50 border-l-4 border-red-500';
    return '';
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">URLクリック分析</h1>
          <p className="text-sm text-gray-500 mt-1">
            リンク別のクリック傾向を分析します
          </p>
        </div>
      </div>

      {/* フィルター */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 法人選択 */}
          {companyOptions.length > 1 ? (
            <Select
              label="法人"
              options={companyOptions}
              value={selectedCompanyId?.toString() || ''}
              onChange={(value) => setSelectedCompanyId(Number(value))}
            />
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
              <div className="px-3 py-2 bg-gray-100 rounded-md text-sm font-medium text-gray-900">
                {companyOptions[0]?.label || '-'}
              </div>
            </div>
          )}

          {/* チャネル */}
          <Select
            label="チャネル"
            options={channelOptions}
            value={channel}
            onChange={(value) => setChannel(value as string)}
          />

          {/* 開始日 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>

          {/* 終了日 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>
        </div>

        {/* 実行ボタン */}
        <div className="mt-4 flex gap-2">
          <Button onClick={fetchData} disabled={loading}>
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                分析中...
              </>
            ) : (
              <>
                <MousePointer2 className="w-4 h-4 mr-2" />
                分析実行
              </>
            )}
          </Button>

          {data && data.data.length > 0 && (
            <Button variant="outline" onClick={exportToCsv}>
              <Download className="w-4 h-4 mr-2" />
              CSV出力
            </Button>
          )}
        </div>
      </Card>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      )}

      {/* 結果表示 */}
      {data && (
        <>
          {/* サマリー */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4">
              <div className="text-sm text-gray-500">総クリック数</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {data.total_clicks.toLocaleString()}
              </div>
            </Card>

            {Object.entries(data.by_channel).map(([ch, count]) => (
              <Card key={ch} className="p-4">
                <div className="text-sm text-gray-500">{ch}</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {count.toLocaleString()}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {data.total_clicks > 0
                    ? `${((count / data.total_clicks) * 100).toFixed(1)}%`
                    : '0%'}
                </div>
              </Card>
            ))}
          </div>

          {/* クリック先ランキング */}
          {data.data.length > 0 && (
            <Card className="overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h2 className="font-semibold text-gray-900">クリック先ランキング</h2>
                <p className="text-sm text-gray-500 mt-1">
                  期間: {data.period.start_date} 〜 {data.period.end_date}
                </p>
              </div>

              <div className="divide-y divide-gray-100">
                {data.data.map((item, index) => (
                  <div
                    key={item.tap_detail}
                    className={`flex items-center justify-between px-4 py-3 ${getHighlightClass(
                      index,
                      data.data.length
                    )}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                        {index + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        <MousePointer2 className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{item.tap_detail}</span>
                      </div>
                      {index === 0 && data.data.length >= 3 && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <TrendingUp className="w-3 h-3" />
                          最多
                        </span>
                      )}
                      {index === data.data.length - 1 && data.data.length >= 3 && (
                        <span className="flex items-center gap-1 text-xs text-red-600">
                          <TrendingDown className="w-3 h-3" />
                          最少
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900">
                          {item.click_count.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">{item.percentage}%</div>
                      </div>

                      {/* 割合バー */}
                      <div className="w-32 hidden sm:block">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* ステップ別詳細 */}
          {data.by_step.length > 0 && (
            <Card className="p-4">
              <h2 className="font-semibold text-gray-900 mb-4">ステップ別詳細</h2>
              <div className="space-y-2">
                {data.by_step.map((step) => {
                  const stepKey = `${step.step_name}_${step.channel}`;
                  return (
                    <StepDetail
                      key={stepKey}
                      step={step}
                      isExpanded={expandedSteps.has(stepKey)}
                      onToggle={() => toggleStep(stepKey)}
                    />
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}

      {/* データなし */}
      {!loading && !error && data && data.data.length === 0 && (
        <Card className="p-8 text-center text-gray-500">
          <MousePointer2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>該当期間のクリックデータがありません</p>
          <p className="text-sm mt-2">
            タップ詳細ラベル（タップ_コメント、タップ_電子書籍など）が登録されていることを確認してください
          </p>
        </Card>
      )}

      {/* 初期状態 */}
      {!loading && !error && !data && (
        <Card className="p-8 text-center text-gray-500">
          <MousePointer2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>「分析実行」ボタンをクリックしてデータを取得してください</p>
        </Card>
      )}
    </div>
  );
}
